import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import {
    Block,
    CheckboxInput,
    FlexDiv,
    LightTooltip,
    ProgressWithNumber,
    TextLead,
    TextTiny,
    ThemedButton,
    palette,
} from '@awarego-ui/components';
import GetAppIcon from '@mui/icons-material/GetApp';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { CircularProgress } from '@mui/material';
import TableServer from '../../../../components/table/TableServer';
import HeaderSection from '../../../../components/header/HeaderSection';
import SubHeaderComponentsList from '../../../../components/header/SubHeaderComponentsList';
import StyledLink from '../../../../components/StyledLink';
import { useStores } from '../../../../hooks/use-stores';
import EmployeesList from '../../../users/EmployeesList';
import { calculateColor } from '../../../../utils/helpers';
import {
    resolveTagColumnFromCompany,
    tagColumnHoldersColumn,
} from '../../../users/users-helpers';

function TrainingEmployees({
    companyId,
    training,
}) {
    const {
        automationStore,
        automationEmployeeListsStore,
        automationEmployeesStoresFactory,
        listStore,
        store,
    } = useStores();
    const { employeesOverviewStore: automationEmployeesStore } =
        automationEmployeesStoresFactory;
    const [query, setQuery] = useState({
        p: 0,
        n: 10,
        order: 'name asc',
        filter: '',
    });

    useEffect(() => {
        automationEmployeeListsStore.loadEmployeeLists(companyId, training.id);
    }, [automationEmployeeListsStore, companyId, training.id]);

    const onChangePagingParams = useCallback((p, n) => {
        automationEmployeeListsStore.setPagingParams(p, n);
        setQuery((current) =>
            current.p === p && current.n === n ? current : { ...current, p, n }
        );
    }, [automationEmployeeListsStore]);

    const onChangeSort = useCallback((sortBy) => {
        if (!sortBy || sortBy.length === 0) return;
        const order = `${sortBy[0].id} ${sortBy[0].desc ? 'desc' : 'asc'}`;
        setQuery((current) =>
            current.order === order ? current : { ...current, order }
        );
    }, []);

    const tagColumn = resolveTagColumnFromCompany(store.currentCompany, [
        ...(automationEmployeeListsStore.employeeLists || []),
        ...(listStore.lists || []),
    ]);

    const listColumns = useMemo(
        () => {
            const cols = [
            {
                Header: 'List',
                accessor: 'listName',
                Cell: observer((x) => (
                    <CheckboxInput
                        label={
                            <StyledLink
                                to={`/users/list/${x.row.original.listId}`}
                                $hoverColor={palette.graphite.onyx}
                                $underlined={true}
                            >
                                {x.value}
                            </StyledLink>
                        }
                        checked={
                            automationEmployeeListsStore.selection
                                .effectiveSelectionOnCurrentPage[
                                x.row.original.listId
                            ]
                        }
                        noYPadding
                        onChange={(e) => {
                            automationEmployeeListsStore.selection.toggleRow(
                                x.row.original,
                                e.target.checked
                            );
                        }}
                    />
                )),
            },
            {
                Header: '# Employees',
                accessor: 'employeesCount',
                Cell: (x) => x.value,
            },
            {
                Header: 'Completion rate',
                accessor: 'completionRate',
                Cell: (x) => {
                    const list = x.row.original;
                    const completed = Number(list.completedUsers || 0);
                    const total = Number(
                        list.totalUsers || list.employeesCount || 0
                    );
                    const percent = total
                        ? Math.round((100 * completed) / total)
                        : 0;
                    const color = calculateColor(percent);
                    const fullCount = Number(
                        list.listMembersCount ?? list.employeesCount ?? 0
                    );
                    const inTrainingCount = Number(
                        list.inTrainingCount ?? total
                    );
                    const showSyncWarning =
                        list.syncDisabled === true &&
                        list.listMembersCount != null &&
                        list.inTrainingCount != null &&
                        fullCount !== inTrainingCount;
                    return (
                        <FlexDiv $alignCenter $noWrap>
                            <span style={{ color }}>
                                {completed} of {total} ({percent}%)
                            </span>
                            {showSyncWarning && (
                                <LightTooltip
                                    title={`List sync is inactive — full list count: ${fullCount}, in training: ${inTrainingCount}.`}
                                >
                                    <span
                                        style={{
                                            display: 'inline-flex',
                                            marginLeft: 4,
                                        }}
                                    >
                                        <ReportProblemOutlinedIcon
                                            style={{
                                                color: palette.vibrant.gold,
                                                fontSize: 16,
                                            }}
                                        />
                                    </span>
                                </LightTooltip>
                            )}
                        </FlexDiv>
                    );
                },
            },
            {
                Header: 'Average progress',
                accessor: 'resilienceScore',
                Cell: (x) => (
                    <ProgressWithNumber value={x.value} height={6} width={120} />
                ),
            },
        ];
            const tagCol = tagColumnHoldersColumn(tagColumn);
            if (tagCol) cols.splice(1, 0, tagCol);
            return cols;
        },
        [automationEmployeeListsStore.selection, tagColumn]
    );

    const fallbackLists = (training?._rawData?.lists || []).map((list) => ({
        ...list,
        listId: list.id,
        listName: list.name,
        employeesCount: list.list_user_count,
        listMembersCount: list.list_user_count,
        inTrainingCount: list.inTrainingCount ?? null,
        completedUsers: list.completedUsers || 0,
        totalUsers: list.list_user_count || 0,
        completionRate: list.list_user_count
            ? Math.round(
                  (100 * (list.completedUsers || 0)) / list.list_user_count
              )
            : 0,
        resilienceScore: list.total
            ? Math.round((100 * list.score) / list.total)
            : 0,
    }));
    const storeLists =
        automationEmployeeListsStore.clientSidePaginationWithSelect.pagedData;
    const listsData =
        storeLists.length > 0
            ? storeLists
            : fallbackLists;
    const listsTotal =
        automationEmployeeListsStore.clientSidePaginationWithSelect.filteredTotal >
        0
            ? automationEmployeeListsStore.clientSidePaginationWithSelect
                  .filteredTotal
            : fallbackLists.length;
    const allListsTotal =
        automationEmployeeListsStore.clientSidePaginationWithSelect.dataTotal ||
        fallbackLists.length;
    const selectedListsCount =
        automationEmployeeListsStore.clientSidePaginationWithSelect
            .selectedCountOnAllPages;

    const hideListsBlock = useMemo(() => {
        if (!listsData.length) return false;
        return listsData.every(
            (l) =>
                l.list_kind === 'hidden_on_demand' ||
                l.listKind === 'hidden_on_demand'
        );
    }, [listsData]);

    const handleSelectLists = (event) => {
        automationEmployeeListsStore.selection.toggleCurrentPageSelected(
            event.target.checked
        );
    };

    return (
        <>
            {!hideListsBlock && (
                <Block gap={24} tablePadding noBorder>
                    <HeaderSection
                    heading={
                        <TextLead bold>{`Lists (${allListsTotal})`}</TextLead>
                    }
                    searchPlaceholder="Search lists"
                    searchValue={
                        automationEmployeeListsStore
                            .clientSidePaginationWithSelect.searchValue
                    }
                    onSearch={(e) =>
                        automationEmployeeListsStore.clientSidePaginationWithSelect.setSearchValue(
                            e.target.value
                        )
                    }
                    onClearSearch={() =>
                        automationEmployeeListsStore.clientSidePaginationWithSelect.setSearchValue(
                            ''
                        )
                    }
                    subHeadings={
                        <SubHeaderComponentsList>
                            <CheckboxInput
                                label={
                                    selectedListsCount === 0 ||
                                    selectedListsCount === allListsTotal
                                        ? `Select all (${allListsTotal})`
                                        : `Selected (${selectedListsCount}/${allListsTotal})`
                                }
                                checked={
                                    automationEmployeeListsStore.selection
                                        .currentPageSelected
                                }
                                indeterminate={
                                    automationEmployeeListsStore.selection
                                        .currentPageSelected &&
                                    automationEmployeeListsStore.selection
                                        .haveDeselectedOnCurrentPage
                                }
                                onChange={handleSelectLists}
                            />
                            {automationEmployeeListsStore.selection.allSelected && (
                                <FlexDiv column>
                                    <TextTiny>
                                        All {allListsTotal} lists selected.
                                    </TextTiny>
                                    <TextTiny
                                        themed
                                        bold
                                        link
                                        onClick={() =>
                                            automationEmployeeListsStore.selection.clearSelection()
                                        }
                                    >
                                        Clear selection
                                    </TextTiny>
                                </FlexDiv>
                            )}
                        </SubHeaderComponentsList>
                    }
                    buttons={
                        <>
                            {training.canRemind && (
                                <ThemedButton
                                    mode="secondary"
                                    size="tiny"
                                    $noTextTransform
                                    disabled={selectedListsCount === 0}
                                    onClick={() => {
                                        automationEmployeesStore.showRemindersModal(
                                            false,
                                            'list',
                                            automationEmployeeListsStore.clientSidePaginationWithSelect.selectedLists.map(
                                                (x) => x.listId
                                            )
                                        );
                                    }}
                                    text="Send reminders"
                                />
                            )}
                            <ThemedButton
                                mode="secondary"
                                size="tiny"
                                $noTextTransform
                                disabled={selectedListsCount === 0}
                                onClick={() =>
                                    automationEmployeeListsStore.downloadCSV(
                                        companyId,
                                        training.id
                                    )
                                }
                                text={
                                    <>
                                        {automationEmployeeListsStore.isDownloading && (
                                            <CircularProgress size={10} />
                                        )}{' '}
                                        Download .csv
                                        <GetAppIcon fontSize="small" />
                                    </>
                                }
                            />
                        </>
                    }
                    excludeBlock
                    marginLeft="16"
                />
                <TableServer
                    columns={listColumns}
                    data={listsData}
                    rowsTotal={listsTotal}
                    pagesTotal={Math.ceil(listsTotal / query.n)}
                    initialPageSize={query.n}
                    defaultSortBy="listName"
                    defaultSortDescent={false}
                    hidePagination={listsTotal <= query.n}
                    onChangePagingParams={onChangePagingParams}
                    onChangeSort={onChangeSort}
                    loadingData={automationEmployeeListsStore.loadingEmployeeLists}
                />
            </Block>
            )}
            <EmployeesList
                companyId={companyId}
                assessmentId={training.id}
                assessmentUsersStore={automationEmployeesStore}
                product={training}
                canRemind={training.canRemind}
                rowMenuVariant="training"
            />
        </>
    );
}

export default observer(TrainingEmployees);
